// src/hooks/useLastSafeRoute.js
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const KEY_STACK = "pdx.safeRouteStack.v1";

/**
 * Normalise known invalid recording subpaths produced by relative navigation.
 *
 * Bug pattern:
 *   /recording/<variant>/camera/guide  (invalid)
 *
 * Correct:
 *   /recording/<variant>/guide
 */
function normalizeSafePath(pathname) {
  if (typeof pathname !== "string") return pathname;

  // Rewrite the invalid nested guide path
  const m = pathname.match(/^\/recording\/([^/]+)\/camera\/guide\/?$/);
  if (m) return `/recording/${m[1]}/guide`;

  return pathname;
}
/**
 * Returns true if a path is considered unsafe to route "back" into.
 */
export function isUnsafePath(pathname) {
  if (!pathname) return true;
  if (pathname === "/") return true;
  if (pathname.startsWith("/auth")) return true;
  return false;
}

function readStack() {
  try {
    const raw = sessionStorage.getItem(KEY_STACK);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((x) => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

function writeStack(stack) {
  try {
    sessionStorage.setItem(KEY_STACK, JSON.stringify(stack));
  } catch {
    // ignore
  }
}

/**
 * Track a stack of "safe" routes only.
 * Logging is intentionally minimal and only prints key events.
 */
export function useLastSafeRoute() {
  const location = useLocation();
  const rawPathname = location?.pathname || "/";
  const pathname = normalizeSafePath(rawPathname);
  const search = location?.search || "";

  useEffect(() => {
    const fullPath = `${pathname}${search || ""}`;

    if (isUnsafePath(pathname)) {
      return;
    }

    const stack = readStack().map(normalizeSafePath);
    const last = stack.length ? stack[stack.length - 1] : null;

    if (stack.length === 0 && pathname.startsWith("/recording")) {
      stack.push("/home");
    }

    if (last !== pathname) {
      stack.push(pathname);
      if (stack.length > 50) stack.splice(0, stack.length - 50);
      writeStack(stack);
    }
  }, [pathname, search]);
}

/**
 * Returns the previous safe route (one step back). Does NOT mutate.
 */
export function peekPrevSafeRoute(fallback = "/home") {
  const stack = readStack();
  const prev = stack.length >= 2 ? stack[stack.length - 2] : null;

  return prev || fallback;
}

/**
 * Pops current and returns previous safe route.
 */
export function popToPrevSafeRoute(fallback = "/home", currentPathname = "") {
  const stack = readStack().map(normalizeSafePath);
  const current = normalizeSafePath(currentPathname);

  if (stack.length < 2) return fallback;

  stack.pop();

  let dest = stack.length ? stack[stack.length - 1] : fallback;
  dest = normalizeSafePath(dest);

  if (!dest || dest === current) dest = fallback;

  writeStack(stack);

  return dest;
}
